import React, { useEffect, useMemo } from 'react';
import {
    FDASearchValue,
    NameAndActiveIngredient,
    getNameAndActiveIngredientKey,
    useFDADrugSearch,
} from '../../../../hooks/api/useFDADrugSearch';
import AutocompleteInput from '../../../v2BaseComponents/Inputs/AutocompleteInput';
import { getIntl } from '../../../../utils/intl';
import Stack, { FormStack } from '../../../v2BaseComponents/Stack';
import { ActionButtonLayout } from '../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer, { mainContentPadding } from '../../../v2BaseComponents/MainContentContainer';
import Text from '../../../v2BaseComponents/Text';
import Divider from '../../../v2BaseComponents/Divider';
import { Image, View } from 'react-native';
import Images from '../../../../Images';
import { Loader } from '../../../v2BaseComponents/Loader';

const intl = getIntl('FDASearchForm', {
    nameOrActiveIngredient: 'Name or Active Ingredient',
    strength: 'Strength',
    form: 'Form',
    route: 'Route',
    noResultsFoundFor: 'No results for "{search}".  Click Add Manually below.',
    minimumSearchLength: 'Minimum search length is 3 characters',
    header: 'Add a Medication',
    subHeader: 'Search by Drug Name or Active Ingredient. Medication results are provided by the FDA database.',
    enterManually: 'Add Manually',
    accept: 'Next',
    additionalInformation: 'Additional Information',
});

export type FDASearchFormValue = Omit<FDASearchValue, 'nameAndActiveIngredient'> & {
    nameAndActiveIngredient: NameAndActiveIngredient | null;
};

export type FDASearchFormOwnProps = {
    onComplete: (value: FDASearchFormValue) => void;
    onSkip: () => void;
};

export type FDASearchFormProps = FDASearchFormOwnProps;

type AutocompleteOption = {
    value: string;
};

type Fields = keyof FDASearchFormValue;

const prioritizedFields: Fields[] = ['nameAndActiveIngredient', 'form', 'route', 'strength'];

const resetDependentFields = (field: Fields, formValue: FDASearchFormValue) => {
    const fieldIndex = prioritizedFields.indexOf(field);
    const fieldsToReset = prioritizedFields.slice(fieldIndex + 1);
    const updatedFormValue = { ...formValue };
    fieldsToReset.forEach((field) => {
        updatedFormValue[field] = null;
    });
    return updatedFormValue;
};

const getEmptyMessage = (searchText: string, enforceMinimum?: boolean): string | undefined => {
    if (!searchText) {
        return undefined;
    } else if (enforceMinimum && searchText.length < 3) {
        return intl('minimumSearchLength');
    } else {
        return intl('noResultsFoundFor', { search: searchText });
    }
};

const StandardFDASearchField: React.FC<{
    title: string;
    value: string | null;
    onChange: (value: string | null) => void;
    options: AutocompleteOption[];
    field: Fields;
    onSearch: (value: string | null) => void;
    isLoading: boolean;
    testID: string;
}> = ({ title, value, onChange, options, onSearch, isLoading, testID }) => {
    const [hasMadeUpdate, setHasMadeUpdate] = React.useState(false);
    const [searchText, setSearchText] = React.useState(value || '');
    const initialUseEffectTriggered = React.useRef(false);
    useEffect(() => {
        if (initialUseEffectTriggered.current) {
            setHasMadeUpdate(true);
        } else {
            initialUseEffectTriggered.current = true;
        }
        onSearch(searchText || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText]);
    const autCompleteValue = useMemo<AutocompleteOption | null>(() => {
        return value ? { value } : null;
    }, [value]);
    const handleChange = (option: AutocompleteOption | null) => {
        setHasMadeUpdate(true);
        onChange(option ? option.value : null);
    };
    useEffect(() => {
        if (!searchText && !isLoading && !hasMadeUpdate && options.length === 1) {
            handleChange(options[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, options, hasMadeUpdate]);

    if (!hasMadeUpdate && isLoading) return null;

    return (
        <AutocompleteInput
            forceFullWidth
            title={title}
            value={autCompleteValue}
            onChange={handleChange}
            filteredItems={options}
            keyExtractor={(option) => option.value}
            displayExtractor={(option) => option.value}
            textOnChange={setSearchText}
            textValue={searchText}
            isLoadingItems={isLoading}
            emptyText={getEmptyMessage(searchText)}
            getOptionTestID={(option) => `autocomplete-item-fda-search-option-${option.value}`}
            getChipTestID={(option) => `chip-fda-search-chip-${option.value}`}
            testID={testID}
        />
    );
};

const NameAndActiveIngredientFDASearchField: React.FC<{
    title: string;
    value: NameAndActiveIngredient | null;
    onChange: (value: NameAndActiveIngredient | null) => void;
    options: NameAndActiveIngredient[];
    onSearch: (value: string | null) => void;
    isLoading: boolean;
    testID: string;
}> = ({ title, value, onChange, options, onSearch, isLoading, testID }) => {
    const [searchText, setSearchText] = React.useState('');
    useEffect(() => {
        onSearch(searchText || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText]);

    const getDisplayValue = (value: NameAndActiveIngredient) => value.name;
    const getSubDisplayValue = (value: NameAndActiveIngredient) => value.genericNameOrActiveIngredient.join(', ');

    return (
        <AutocompleteInput
            forceFullWidth
            title={title}
            value={value}
            onChange={onChange}
            filteredItems={options}
            keyExtractor={getNameAndActiveIngredientKey}
            displayExtractor={getDisplayValue}
            subDisplayExtractor={getSubDisplayValue}
            getOptionTestID={(option) =>
                `autocomplete-item-fda-search-option-${getDisplayValue(option)}-${getSubDisplayValue(option)}`
            }
            getChipTestID={(option) => `chip-fda-search-chip-${getDisplayValue(option)}-${getSubDisplayValue(option)}`}
            textOnChange={setSearchText}
            textValue={searchText}
            isLoadingItems={isLoading}
            emptyText={getEmptyMessage(searchText, true)}
            testID={testID}
        />
    );
};

type SearchFieldAndValue = {
    field: Fields;
    value: string;
};

const FDASearchForm: React.FC<FDASearchFormProps> = (props) => {
    const [formValue, setFormValue] = React.useState<FDASearchFormValue>({
        nameAndActiveIngredient: null,
        strength: null,
        form: null,
        route: null,
    });
    const [searchFieldAndValue, setSearchFieldAndValue] = React.useState<SearchFieldAndValue | null>(null);
    const getOnSearchHandler = (field: Fields) => (value: string | null) => {
        setSearchFieldAndValue(value ? { field, value: value } : null);
        const updatedFormValue = resetDependentFields(field, { ...formValue });
        setFormValue({ ...updatedFormValue, [field]: null });
    };
    const searchValue = useMemo<FDASearchValue>(() => {
        if (!searchFieldAndValue) return formValue;
        return {
            ...formValue,
            [searchFieldAndValue.field]: searchFieldAndValue.value,
        };
    }, [searchFieldAndValue, formValue]);
    const searchResult = useFDADrugSearch({ value: searchValue });
    const strengthOptions = useMemo(() => {
        return searchResult.searchResults.strength.map((strength) => ({ value: strength }));
    }, [searchResult.searchResults.strength]);
    const formOptions = useMemo(() => {
        return searchResult.searchResults.form.map((form) => ({ value: form }));
    }, [searchResult.searchResults.form]);
    const routeOptions = useMemo(() => {
        return searchResult.searchResults.route.map((route) => ({ value: route }));
    }, [searchResult.searchResults.route]);

    const shouldRenderLoader = searchResult.isLoading && !searchFieldAndValue;

    return (
        <ActionButtonLayout
            scrollable
            actions={[
                {
                    title: intl('enterManually'),
                    variant: 'secondary',
                    onPress: () => props.onSkip(),
                    icon: 'Edit2',
                    testID: 'fda-search-enter-manually',
                },
                {
                    title: intl('accept'),
                    variant: 'primary',
                    onPress: () => props.onComplete(formValue),
                    icon: 'ArrowRight',
                    isDisabled: !formValue.nameAndActiveIngredient,
                    testID: 'fda-search-enter-accept',
                },
            ]}
        >
            <MainContentContainer style={{ width: '100%', minHeight: '100%', flex: 1 }}>
                <Stack vertical fullWidth flex={1} space={8}>
                    <Stack vertical fullWidth>
                        <Stack horizontal align="center" justify="space-between" space={4} fullWidth>
                            <Text variant="bold-h4">{intl('header')}</Text>
                            <Image source={Images.FDALogo} style={{ width: 80, height: 36.27 }} />
                        </Stack>
                        <Divider
                            horizontal
                            style={{
                                marginLeft: -1 * mainContentPadding,
                                marginRight: -1 * mainContentPadding,
                                width: '120%',
                            }}
                        />
                    </Stack>
                    <Text variant="body-2" style={{ width: '100%' }}>
                        {intl('subHeader')}
                    </Text>
                    <FormStack style={{ minHeight: '100%', flex: 1 }}>
                        <NameAndActiveIngredientFDASearchField
                            title={intl('nameOrActiveIngredient')}
                            value={formValue.nameAndActiveIngredient}
                            onChange={(value) =>
                                setFormValue(
                                    resetDependentFields('nameAndActiveIngredient', {
                                        ...formValue,
                                        nameAndActiveIngredient: value,
                                    }),
                                )
                            }
                            options={searchResult.searchResults.nameAndActiveIngredient}
                            onSearch={(value) => getOnSearchHandler('nameAndActiveIngredient')(value)}
                            isLoading={searchResult.isLoading}
                            testID="fda-search-name-and-active-ingredient"
                        />
                        {formValue.nameAndActiveIngredient ? (
                            <StandardFDASearchField
                                title={intl('form')}
                                value={formValue.form}
                                onChange={(value) =>
                                    setFormValue(resetDependentFields('form', { ...formValue, form: value }))
                                }
                                options={formOptions}
                                field="form"
                                onSearch={getOnSearchHandler('form')}
                                isLoading={searchResult.isLoading}
                                testID="fda-search-form"
                            />
                        ) : null}
                        {formValue.form ? (
                            <StandardFDASearchField
                                title={intl('route')}
                                value={formValue.route}
                                onChange={(value) =>
                                    setFormValue(resetDependentFields('route', { ...formValue, route: value }))
                                }
                                options={routeOptions}
                                field="route"
                                onSearch={getOnSearchHandler('route')}
                                isLoading={searchResult.isLoading}
                                testID="fda-search-route"
                            />
                        ) : null}
                        {formValue.route ? (
                            <StandardFDASearchField
                                title={intl('strength')}
                                value={formValue.strength}
                                onChange={(value) =>
                                    setFormValue(resetDependentFields('strength', { ...formValue, strength: value }))
                                }
                                options={strengthOptions}
                                field="strength"
                                onSearch={getOnSearchHandler('strength')}
                                isLoading={searchResult.isLoading}
                                testID="fda-search-strength"
                            />
                        ) : null}
                        {shouldRenderLoader ? (
                            <View style={{ width: '100%', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                                <Loader size="sm" />
                            </View>
                        ) : null}
                    </FormStack>
                </Stack>
            </MainContentContainer>
        </ActionButtonLayout>
    );
};

export default React.memo(FDASearchForm);
